import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ArrowLeft } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { supabase } from "@/integrations/supabase/client";

type DiagramDetail = {
  id: string;
  name: string;
  created_at: string;
  modified_at: string | null;
};

type DiagramComponent = {
  component_type_name: string;
  title: string;
  created_at: string;
};

export default function DiagramDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [diagram, setDiagram] = useState<DiagramDetail | null>(null);
  const [components, setComponents] = useState<DiagramComponent[]>([]);

  useEffect(() => {
    if (id) {
      loadDiagramDetails();
      loadDiagramComponents();
    }
  }, [id]);

  const loadDiagramDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('diagrams')
        .select('id, name, created_at, modified_at')
        .eq('id', id)
        .single();

      if (error) throw error;
      setDiagram(data);
    } catch (error) {
      console.error('Error loading diagram details:', error);
    }
  };

  const loadDiagramComponents = async () => {
    try {
      const { data, error } = await supabase
        .from('diagram_components')
        .select(`
          created_at,
          title,
          components (
            component_types (
              name
            )
          )
        `)
        .eq('id_diagram', id)
        .order('title');

      if (error) throw error;

      const formattedComponents = data.map((item: any) => ({
        component_type_name: item.components?.component_types?.name || '',
        title: item.title || '',
        created_at: item.created_at,
      }));

      setComponents(formattedComponents);
    } catch (error) {
      console.error('Error loading diagram components:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToDiagrams = () => {
    navigate('/diagramas');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-[#189ab4]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToDiagrams}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-0.5">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">{diagram?.name || "Diagrama"}</h1>
            <p className="text-muted-foreground">Visualize os detalhes e componentes do diagrama.</p>
          </div>
        </div>
      </div>

      <Card className="border border-border/40 shadow-sm">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold mb-4 text-foreground">Informações do Diagrama</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nome:</p>
                  <p className="font-medium text-foreground">{diagram?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Criado em:</p>
                  <p className="font-medium text-foreground">{formatDate(diagram?.created_at || null)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Última modificação:</p>
                  <p className="font-medium text-foreground">{formatDate(diagram?.modified_at || null)}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4 text-foreground">Componentes do Diagrama</h2>
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/5 hover:bg-primary/10">
                    <TableHead className="text-primary font-medium">Tipo de Componente</TableHead>
                    <TableHead className="text-primary font-medium">Título</TableHead>
                    <TableHead className="text-primary font-medium">Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {components.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8">
                        <p className="text-sm text-muted-foreground">Nenhum componente encontrado.</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    components.map((component, index) => (
                      <TableRow key={index} className="hover:bg-muted/50">
                        <TableCell>{component.component_type_name}</TableCell>
                        <TableCell>{component.title}</TableCell>
                        <TableCell>{formatDate(component.created_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
